import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * THE TWO THINGS A SCREEN NEEDS TO TELL THE SHELL.
 *
 * The shell is rendered by the authenticated layout and every screen is its
 * child, so a screen that has learned something the chrome must show has no
 * ordinary way to say it. Two cases, and only two:
 *
 *   `openAskAi`       — a screen offers Ask TESSMA AI as an action of its own
 *                       (Screen 00's Intelligence card). The drawer belongs to
 *                       the layout; the button belongs to the screen.
 *   `reportUnread`    — the bell is in the shell on every page, but the count
 *                       arrives inside the dashboard's own composed read model,
 *                       which only Screen 00 requests.
 *
 * WHY A CONTEXT RATHER THAN A PROP. A prop would have to be threaded through
 * TanStack Router's outlet, which does not carry them. This is a bridge, not a
 * store: nothing lives here that a screen could not have rendered itself, and
 * no state here decides what anybody may see.
 *
 * THE COUNT IS `undefined` UNTIL SOMEBODY REPORTS ONE, and undefined is not
 * zero. The shell already draws those differently: no badge at all means the
 * question has not been asked, and a zero means it was asked and answered.
 */
export interface ShellBridge {
  readonly openAskAi: () => void;
  readonly reportUnread: (count: number) => void;
  readonly unread: number | undefined;
}

const NOOP: ShellBridge = {
  openAskAi: () => undefined,
  reportUnread: () => undefined,
  unread: undefined,
};

const Context = createContext<ShellBridge>(NOOP);

/** Outside the authenticated layout — in a test, say — this does nothing. */
export function useShellBridge(): ShellBridge {
  return useContext(Context);
}

export function ShellBridgeProvider({
  openAskAi,
  children,
}: {
  readonly openAskAi: () => void;
  readonly children: ReactNode;
}) {
  const [unread, setUnread] = useState<number | undefined>(undefined);
  const value = useMemo<ShellBridge>(
    () => ({ openAskAi, reportUnread: setUnread, unread }),
    [openAskAi, unread],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
